// src/api/offers.ts

import { apiCweb } from './api-cweb';
import { appLog } from '~src/utils/log';
import { InstantMessage } from './movements';

export type ExpandedParams = {
  offerId: number;
  skip?: number;
  take?: number;
  sort?: string;
} & BaseProjectParams;

export type BaseProjectParams = {
  projectId: number;
  tenderId: number;
};

// =================================== OFFERS
// Get all offers
export const getAllOffers = async ({
  projectId,
  tenderId,
  contract_ids,
  share_keys,
  skip,
  take,
}: {
  projectId: number | undefined;
  tenderId: number | string;
  contract_ids?: string[];
  share_keys?: string[];
  skip?: number;
  take?: number;
}) => {
  const res = await apiCweb.get(
    `/projects/${projectId}/tenders/${tenderId}/offers`,
    {
      params: {
        contract_ids,
        share_keys,
        skip,
        take,
      },
    }
  );
  appLog('getAllOffers ->', res.data);
  return res.data;
};

// Get a SINGLE offer
export const getOffer = async ({
  projectId,
  tenderId,
  offerId,
}: {
  projectId: number | undefined;
  tenderId: number | string;
  offerId: number | string;
}) => {
  const res = await apiCweb.get(
    `/projects/${projectId}/tenders/${tenderId}/offers/${offerId}`
  );
  appLog('getOffer ->', res.data);
  return res.data;
};

// Get ALL vouchers FROM offer
export const getOfferVouchers = async ({
  projectId,
  tenderId,
  offerId,
}: {
  projectId: number | undefined;
  tenderId: number | string;
  offerId: number | null;
}) => {
  const res = await apiCweb.get(
    `/projects/${projectId}/tenders/${tenderId}/offers/${offerId}/voucher_responses`
  );
  appLog('getOfferVouchers ->', res.data);
  return res.data;
};

// Get all offers with contracts
export const getAllOffersContracts = async ({
  projectId,
  tenderId,
}: {
  projectId: number | undefined;
  tenderId: number | string;
}) => {
  const res = await apiCweb.get(
    `/projects/${projectId}/tenders/${tenderId}/contract_offers`
  );
  appLog('getAllOffersContracts ->', res.data);
  return res.data;
};

export type ContractOffersCoreItem = {
  contract_id: number;
  contract_name: string;
  contract_description?: string;
  contract_reference?: string;
  summary: {
    created: number;
    submitted: number;
  };
  offers: Array<{
    id: number;
    state: number;
    share_key: string | null;
    share_key_title: string | null;
    offer_created_at: number;
    offer_created_by_name: string;
    offer_updated_at: number;
    offer_updated_by_name: string;
    offeror_identity_information: {
      name: string;
      email: string;
      company: string;
      address: string;
      zipcode: string;
      town: string;
      phone_country_code: string;
      phone_number: string;
      mobile_country_code: string;
      mobile_number: string;
    };
    voucher_response?: {
      offer_id: number;
      contract_id: number;
      required_total: number;
      required_responded: number;
      optional_total: number;
      optional_responded: number;
    } | null;
  }>;
};

export async function getContractOffersCore(params: {
  projectId: number | string;
  tenderId: number | string;
}): Promise<ContractOffersCoreItem[]> {
  const { projectId, tenderId } = params;

  const res = await apiCweb.get(
    `projects/${projectId}/tenders/${tenderId}/contract_offers_core`
  );

  return res.data;
}

// OFFER'S REVIEWS ====
export type ReviewInstance = {
  id: number;
  star_rating: number;
  content: string;
  created_user_full_name: string;
  state: number;
  created_at: number;
}[];

export async function getAllOffersReviews({
  projectId,
  tenderId,
  offerId,
  skip,
  take,
  sort,
}: ExpandedParams): Promise<ReviewInstance> {
  appLog('getAllOffersReviews ->', {
    projectId,
    tenderId,
    offerId,
    skip,
    take,
    sort,
  });
  const res = await apiCweb.get(
    `/projects/${projectId}/tenders/${tenderId}/offers/${offerId}/review_stars`,
    {
      params: {
        skip,
        take,
        sort,
      },
    }
  );

  return res.data;
}

export type postOfferReviewParams = {
  offerId: number;
  body: {
    star_rating: number;
    content: string;
  };
} & BaseProjectParams;

export async function postOfferReview({
  projectId,
  tenderId,
  offerId,
  body,
}: postOfferReviewParams) {
  appLog('postOfferReview ->', { projectId, tenderId, offerId, body });
  const res = await apiCweb.post(
    `/projects/${projectId}/tenders/${tenderId}/offers/${offerId}/review_stars`,
    body
  );

  return res.status;
}

export type deleteOffersReviewParams = {
  offerId: number;
  reviewId: number;
} & BaseProjectParams;

export async function deleteOfferReview({
  projectId,
  tenderId,
  offerId,
  reviewId,
}: deleteOffersReviewParams) {
  appLog('deleteOfferReview ->', { projectId, tenderId, offerId, reviewId });
  const res = await apiCweb.delete(
    `/projects/${projectId}/tenders/${tenderId}/offers/${offerId}/review_stars/${reviewId}`
  );

  return res.status;
}

// ==== CONTRACT SCREENING / EVALUATION ====
export type EvaluationEnum =
  | 'unreviewed'
  | 'considered'
  | 'conditional'
  | 'selected'
  | 'rejected'
  | 'priority';

export type ContractOfferScreeningEvaluationParams = {
  contractId: number;
  offerId: number;
} & BaseProjectParams;

export async function setContractOfferScreeningEvaluationSelected({
  projectId,
  tenderId,
  contractId,
  offerId,
}: ContractOfferScreeningEvaluationParams) {
  appLog('setContractOfferScreeningEvaluationSelected ->', {
    projectId,
    tenderId,
    contractId,
    offerId,
  });

  const res = await apiCweb.put(
    `/projects/${projectId}/tenders/${tenderId}/contracts/${contractId}/screening/offers/${offerId}/evaluation/selected`,
    {}
  );

  return res.status;
}

export async function setContractOfferScreeningEvaluationRejected({
  projectId,
  tenderId,
  contractId,
  offerId,
}: ContractOfferScreeningEvaluationParams) {
  appLog('setContractOfferScreeningEvaluationRejected ->', {
    projectId,
    tenderId,
    contractId,
    offerId,
  });

  const res = await apiCweb.put(
    `/projects/${projectId}/tenders/${tenderId}/contracts/${contractId}/screening/offers/${offerId}/evaluation/rejected`,
    {}
  );

  return res.status;
}

export type FinalizeContractScreeningParams = {
  contractId: number;
} & BaseProjectParams;

export async function finalizeContractScreening({
  projectId,
  tenderId,
  contractId,
}: FinalizeContractScreeningParams) {
  appLog('finalizeContractScreening ->', {
    projectId,
    tenderId,
    contractId,
  });

  const res = await apiCweb.put(
    `/projects/${projectId}/tenders/${tenderId}/contracts/${contractId}/screening/finalize`,
    {}
  );

  return res.status;
}

export type AcceptAllContractScreeningOffersParams = {
  contractId: number;
} & BaseProjectParams;

export async function acceptAllContractScreeningOffers({
  projectId,
  tenderId,
  contractId,
}: AcceptAllContractScreeningOffersParams) {
  appLog('acceptAllContractScreeningOffers ->', {
    projectId,
    tenderId,
    contractId,
  });

  const res = await apiCweb.put(
    `/projects/${projectId}/tenders/${tenderId}/contracts/${contractId}/screening/accept_all_offers`,
    {}
  );

  return res.status;
}

export type SkipRemainingContractScreeningParams = {
  contractId: number;
} & BaseProjectParams;

export async function skipRemainingContractScreening({
  projectId,
  tenderId,
  contractId,
}: SkipRemainingContractScreeningParams) {
  appLog('skipRemainingContractScreening ->', {
    projectId,
    tenderId,
    contractId,
  });

  const res = await apiCweb.put(
    `/projects/${projectId}/tenders/${tenderId}/contracts/${contractId}/screening/skip_remaining`,
    {}
  );

  return res.status;
}

export type GetContractScreeningOffersParams = {
  contractId: number;
  shareKeys?: string[];
  screeningStates?: number[];
  includeDashboard?: boolean;
  skip?: number;
  take?: number;
} & BaseProjectParams;

export async function getContractScreeningOffers({
  projectId,
  tenderId,
  contractId,
  shareKeys,
  screeningStates,
  includeDashboard,
  skip,
  take,
}: GetContractScreeningOffersParams) {
  appLog('getContractScreeningOffers ->', {
    projectId,
    tenderId,
    contractId,
    shareKeys,
    screeningStates,
    includeDashboard,
    skip,
    take,
  });

  const res = await apiCweb.get(
    `/projects/${projectId}/tenders/${tenderId}/contracts/${contractId}/screening/offers`,
    {
      params: {
        share_keys: shareKeys?.join(','),
        screening_states: screeningStates?.join(','),
        include_dashboard: includeDashboard,
        skip,
        take,
      },
    }
  );

  return res.data;
}

export type ScreeningOfferDataParams = {
  contractId: number;
  offerIds?: number[];
} & BaseProjectParams;

export async function getContractScreeningOffersData({
  projectId,
  tenderId,
  contractId,
  offerIds,
}: ScreeningOfferDataParams) {
  const res = await apiCweb.get(
    `/projects/${projectId}/tenders/${tenderId}/contracts/${contractId}/screening/offers_data`,
    { params: { offer_ids: offerIds?.join(',') } }
  );
  appLog('getContractScreeningOffersData ->', res.data);
  return res.data;
}

export type GetContractEvaluationOffersParams = {
  contractId: number;
  shareKeys?: string[];
  evaluationStates?: number[];
  includeDashboard?: boolean;
  skip?: number;
  take?: number;
} & BaseProjectParams;

export async function getContractEvaluationOffers({
  projectId,
  tenderId,
  contractId,
  shareKeys,
  evaluationStates,
  includeDashboard,
  skip,
  take,
}: GetContractEvaluationOffersParams) {
  appLog('getContractEvaluationOffers ->', {
    projectId,
    tenderId,
    contractId,
    shareKeys,
    evaluationStates,
    includeDashboard,
    skip,
    take,
  });

  const res = await apiCweb.get(
    `/projects/${projectId}/tenders/${tenderId}/contracts/${contractId}/evaluation/offers`,
    {
      params: {
        share_keys: shareKeys?.join(','),
        evaluation_states: evaluationStates?.join(','),
        include_dashboard: includeDashboard,
        skip,
        take,
      },
    }
  );

  return res.data;
}

export type SetContractOfferEvaluationParams = {
  contractId: number;
  offerId: number;
  body: {
    state: EvaluationEnum;
  };
} & BaseProjectParams;

export async function setContractOfferEvaluation({
  projectId,
  tenderId,
  contractId,
  offerId,
  body,
}: SetContractOfferEvaluationParams) {
  appLog('setContractOfferEvaluation ->', {
    projectId,
    tenderId,
    contractId,
    offerId,
    body,
  });

  const res = await apiCweb.put(
    `/projects/${projectId}/tenders/${tenderId}/contracts/${contractId}/evaluation/offers/${offerId}/evaluation`,
    body
  );

  return res.status;
}

export type FinalizeContractEvaluationParams = {
  contractId: number;
} & BaseProjectParams;

export async function finalizeContractEvaluation({
  projectId,
  tenderId,
  contractId,
}: FinalizeContractEvaluationParams) {
  appLog('finalizeContractEvaluation ->', { projectId, tenderId, contractId });

  const res = await apiCweb.put(
    `/projects/${projectId}/tenders/${tenderId}/contracts/${contractId}/evaluation/finalize`,
    {}
  );

  return res.status;
}

//==== OFFER'S EVALUATION MESSAGES ====

export type EvaluationInstantMessageReceiver = {
  email: string;
  full_name: string;
  company_name: string;
};

export type EvaluationBaseParams = BaseProjectParams & {
  contractId: number;
};

export type EvaluationInstantMessageBody = {
  title: string;
  message: string;
  reference: string;
};

const evalBase = (projectId: number, tenderId: number, contractId: number) =>
  `/projects/${projectId}/tenders/${tenderId}/contracts/${contractId}/evaluation/instant_messages`;

/**
 * Get receivers for evaluation-phase instant messages
 */
export const getEvaluationInstantMessageReceivers = async ({
  projectId,
  tenderId,
  contractId,
}: EvaluationBaseParams): Promise<EvaluationInstantMessageReceiver[]> => {
  appLog('getEvaluationInstantMessageReceivers #1 ->', {
    projectId,
    tenderId,
    contractId,
  });

  const res = await apiCweb.get<EvaluationInstantMessageReceiver[]>(
    `${evalBase(projectId, tenderId, contractId)}/receivers`
  );

  appLog('getEvaluationInstantMessageReceivers #2 ->', res.data);
  return res.data;
};

/**
 * Get list of sent evaluation-phase instant messages
 */
export const getEvaluationInstantMessages = async ({
  projectId,
  tenderId,
  contractId,
  skip,
  take,
  reference,
  sort,
}: EvaluationBaseParams & {
  skip?: number;
  take?: number;
  reference?: string;
  sort?: string;
}): Promise<InstantMessage[]> => {
  appLog('getEvaluationInstantMessages #1 ->', {
    projectId,
    tenderId,
    contractId,
  });

  const res = await apiCweb.get<InstantMessage[]>(
    evalBase(projectId, tenderId, contractId),
    { params: { skip, take, reference, sort } }
  );

  appLog('getEvaluationInstantMessages #2 ->', res.data);
  return res.data;
};

/**
 * Get a single sent evaluation-phase instant message by ID
 */
export const getEvaluationInstantMessage = async ({
  projectId,
  tenderId,
  contractId,
  instantMessageId,
}: EvaluationBaseParams & {
  instantMessageId: number;
}): Promise<InstantMessage> => {
  appLog('getEvaluationInstantMessage #1 ->', {
    projectId,
    tenderId,
    contractId,
    instantMessageId,
  });

  const res = await apiCweb.get<InstantMessage>(
    `${evalBase(projectId, tenderId, contractId)}/${instantMessageId}`
  );

  appLog('getEvaluationInstantMessage #2 ->', res.data);
  return res.data;
};

/**
 * Get email HTML preview of a sent evaluation instant message
 */
export const getEvaluationInstantMessageEmailPreview = async ({
  projectId,
  tenderId,
  contractId,
  instantMessageId,
  lang,
}: EvaluationBaseParams & {
  instantMessageId: number;
  lang?: string;
}): Promise<string> => {
  appLog('getEvaluationInstantMessageEmailPreview #1 ->', {
    projectId,
    tenderId,
    contractId,
    instantMessageId,
    lang,
  });

  const res = await apiCweb.get<string>(
    `${evalBase(
      projectId,
      tenderId,
      contractId
    )}/${instantMessageId}/email_preview_as_html`,
    { params: { lang } }
  );

  appLog('getEvaluationInstantMessageEmailPreview #2 ->', res.status);
  return res.data;
};

/**
 * Get Amazon S3 location of a sent evaluation instant message attachment
 */
export const getEvaluationInstantMessageAttachmentLocation = async ({
  projectId,
  tenderId,
  contractId,
  instantMessageId,
  fileGuid,
}: EvaluationBaseParams & {
  instantMessageId: number;
  fileGuid: string;
}): Promise<string> => {
  appLog('getEvaluationInstantMessageAttachmentLocation #1 ->', {
    projectId,
    tenderId,
    contractId,
    instantMessageId,
    fileGuid,
  });

  const res = await apiCweb.head(
    `${evalBase(
      projectId,
      tenderId,
      contractId
    )}/${instantMessageId}/attachments/${fileGuid}/location`
  );

  const location =
    (res.headers?.location as string) ||
    (res.headers?.Location as string) ||
    '';

  appLog('getEvaluationInstantMessageAttachmentLocation #2 ->', {
    status: res.status,
    location,
  });
  return location;
};

/**
 * Get list of evaluation instant message drafts
 */
export const getEvaluationInstantMessageDrafts = async ({
  projectId,
  tenderId,
  contractId,
}: EvaluationBaseParams): Promise<InstantMessage[]> => {
  appLog('getEvaluationInstantMessageDrafts #1 ->', {
    projectId,
    tenderId,
    contractId,
  });

  const res = await apiCweb.get<InstantMessage[]>(
    `${evalBase(projectId, tenderId, contractId)}/drafts`
  );

  appLog('getEvaluationInstantMessageDrafts #2 ->', res.data);
  return res.data;
};

/**
 * Create a new evaluation instant message draft
 */
export const createEvaluationInstantMessageDraft = async ({
  projectId,
  tenderId,
  contractId,
}: EvaluationBaseParams): Promise<InstantMessage> => {
  appLog('createEvaluationInstantMessageDraft #1 ->', {
    projectId,
    tenderId,
    contractId,
  });

  const res = await apiCweb.post<InstantMessage>(
    `${evalBase(projectId, tenderId, contractId)}/drafts`,
    {}
  );

  appLog('createEvaluationInstantMessageDraft #2 ->', res.data);
  return res.data;
};

/**
 * Get a single evaluation instant message draft by ID
 */
export const getEvaluationInstantMessageDraft = async ({
  projectId,
  tenderId,
  contractId,
  draftId,
}: EvaluationBaseParams & {
  draftId: number;
}): Promise<InstantMessage> => {
  appLog('getEvaluationInstantMessageDraft #1 ->', {
    projectId,
    tenderId,
    contractId,
    draftId,
  });

  const res = await apiCweb.get<InstantMessage>(
    `${evalBase(projectId, tenderId, contractId)}/drafts/${draftId}`
  );

  appLog('getEvaluationInstantMessageDraft #2 ->', res.data);
  return res.data;
};

/**
 * Get email HTML preview of an evaluation instant message draft
 */
export const getEvaluationInstantMessageDraftEmailPreview = async ({
  projectId,
  tenderId,
  contractId,
  draftId,
  lang,
}: EvaluationBaseParams & {
  draftId: number;
  lang?: string;
}): Promise<string> => {
  appLog('getEvaluationInstantMessageDraftEmailPreview #1 ->', {
    projectId,
    tenderId,
    contractId,
    draftId,
    lang,
  });

  const res = await apiCweb.get<string>(
    `${evalBase(
      projectId,
      tenderId,
      contractId
    )}/drafts/${draftId}/email_preview_as_html`,
    { params: { lang } }
  );

  appLog('getEvaluationInstantMessageDraftEmailPreview #2 ->', res.status);
  return res.data;
};

/**
 * Send an evaluation instant message draft to recipients
 */
export const sendEvaluationInstantMessageDraft = async ({
  projectId,
  tenderId,
  contractId,
  draftId,
  body,
}: EvaluationBaseParams & {
  draftId: number;
  body: EvaluationInstantMessageBody;
}) => {
  appLog('sendEvaluationInstantMessageDraft #1 ->', {
    projectId,
    tenderId,
    contractId,
    draftId,
    body,
  });

  const res = await apiCweb.put(
    `${evalBase(projectId, tenderId, contractId)}/drafts/${draftId}`,
    body,
    { params: { action: 'send' } }
  );

  appLog('sendEvaluationInstantMessageDraft #2 ->', res.status);
  return res.status;
};

/**
 * Update an evaluation instant message draft content
 */
export const updateEvaluationInstantMessageDraft = async ({
  projectId,
  tenderId,
  contractId,
  draftId,
  body,
}: EvaluationBaseParams & {
  draftId: number;
  body: EvaluationInstantMessageBody;
}) => {
  appLog('updateEvaluationInstantMessageDraft #1 ->', {
    projectId,
    tenderId,
    contractId,
    draftId,
    body,
  });

  const res = await apiCweb.put(
    `${evalBase(projectId, tenderId, contractId)}/drafts/${draftId}`,
    body,
    { params: { action: 'update' } }
  );

  appLog('updateEvaluationInstantMessageDraft #2 ->', res.status);
  return res.status;
};

/**
 * Remove an attachment from an evaluation instant message draft
 */
export const deleteEvaluationInstantMessageDraftAttachment = async ({
  projectId,
  tenderId,
  contractId,
  draftId,
  fileGuid,
}: EvaluationBaseParams & {
  draftId: number;
  fileGuid: string;
}) => {
  appLog('deleteEvaluationInstantMessageDraftAttachment #1 ->', {
    projectId,
    tenderId,
    contractId,
    draftId,
    fileGuid,
  });

  const res = await apiCweb.delete(
    `${evalBase(
      projectId,
      tenderId,
      contractId
    )}/drafts/${draftId}/attachments/${fileGuid}`
  );

  appLog('deleteEvaluationInstantMessageDraftAttachment #2 ->', res.status);
  return res.status;
};

/**
 * Get Amazon S3 location of an evaluation instant message draft attachment
 */
export const getEvaluationInstantMessageDraftAttachmentLocation = async ({
  projectId,
  tenderId,
  contractId,
  draftId,
  fileGuid,
}: EvaluationBaseParams & {
  draftId: number;
  fileGuid: string;
}): Promise<string> => {
  appLog('getEvaluationInstantMessageDraftAttachmentLocation #1 ->', {
    projectId,
    tenderId,
    contractId,
    draftId,
    fileGuid,
  });

  const res = await apiCweb.head(
    `${evalBase(
      projectId,
      tenderId,
      contractId
    )}/drafts/${draftId}/attachments/${fileGuid}/location`
  );

  const location =
    (res.headers?.location as string) ||
    (res.headers?.Location as string) ||
    '';

  appLog('getEvaluationInstantMessageDraftAttachmentLocation #2 ->', {
    status: res.status,
    location,
  });
  return location;
};

//==== OFFER'S EVALUATION CURRENT INSTANT MESSAGE ====

const evalCurrentBase = (
  projectId: number,
  tenderId: number,
  contractId: number
) =>
  `/projects/${projectId}/tenders/${tenderId}/contracts/${contractId}/evaluation/current_instant_message`;

/**
 * Get or create the current evaluation instant message
 */
export const getEvaluationCurrentInstantMessage = async ({
  projectId,
  tenderId,
  contractId,
}: EvaluationBaseParams): Promise<InstantMessage> => {
  appLog('getEvaluationCurrentInstantMessage #1 ->', {
    projectId,
    tenderId,
    contractId,
  });

  const res = await apiCweb.get<InstantMessage>(
    evalCurrentBase(projectId, tenderId, contractId)
  );

  appLog('getEvaluationCurrentInstantMessage #2 ->', res.data);
  return res.data;
};

/**
 * Get email HTML preview of the current evaluation instant message
 */
export const getEvaluationCurrentInstantMessageEmailPreview = async ({
  projectId,
  tenderId,
  contractId,
  lang,
}: EvaluationBaseParams & {
  lang?: string;
}): Promise<string> => {
  appLog('getEvaluationCurrentInstantMessageEmailPreview #1 ->', {
    projectId,
    tenderId,
    contractId,
    lang,
  });

  const res = await apiCweb.get<string>(
    `${evalCurrentBase(projectId, tenderId, contractId)}/email_preview_as_html`,
    { params: { lang } }
  );

  appLog('getEvaluationCurrentInstantMessageEmailPreview #2 ->', res.status);
  return res.data;
};

/**
 * Send the current evaluation instant message to recipients
 */
export const sendEvaluationCurrentInstantMessage = async ({
  projectId,
  tenderId,
  contractId,
  body,
}: EvaluationBaseParams & {
  body: EvaluationInstantMessageBody;
}) => {
  appLog('sendEvaluationCurrentInstantMessage #1 ->', {
    projectId,
    tenderId,
    contractId,
    body,
  });

  const res = await apiCweb.put(
    evalCurrentBase(projectId, tenderId, contractId),
    body,
    { params: { action: 'send' } }
  );

  appLog('sendEvaluationCurrentInstantMessage #2 ->', res.status);
  return res.status;
};

/**
 * Update the current evaluation instant message content
 */
export const updateEvaluationCurrentInstantMessage = async ({
  projectId,
  tenderId,
  contractId,
  body,
}: EvaluationBaseParams & {
  body: EvaluationInstantMessageBody;
}) => {
  appLog('updateEvaluationCurrentInstantMessage #1 ->', {
    projectId,
    tenderId,
    contractId,
    body,
  });

  const res = await apiCweb.put(
    evalCurrentBase(projectId, tenderId, contractId),
    body,
    { params: { action: 'update' } }
  );

  appLog('updateEvaluationCurrentInstantMessage #2 ->', res.status);
  return res.status;
};

/**
 * Remove an attachment from the current evaluation instant message
 */
export const deleteEvaluationCurrentInstantMessageAttachment = async ({
  projectId,
  tenderId,
  contractId,
  fileGuid,
}: EvaluationBaseParams & {
  fileGuid: string;
}) => {
  appLog('deleteEvaluationCurrentInstantMessageAttachment #1 ->', {
    projectId,
    tenderId,
    contractId,
    fileGuid,
  });

  const res = await apiCweb.delete(
    `${evalCurrentBase(
      projectId,
      tenderId,
      contractId
    )}/attachments/${fileGuid}`
  );

  appLog('deleteEvaluationCurrentInstantMessageAttachment #2 ->', res.status);
  return res.status;
};

/**
 * Get Amazon S3 location of the current evaluation instant message attachment
 */
export const getEvaluationCurrentInstantMessageAttachmentLocation = async ({
  projectId,
  tenderId,
  contractId,
  fileGuid,
}: EvaluationBaseParams & {
  fileGuid: string;
}): Promise<string> => {
  appLog('getEvaluationCurrentInstantMessageAttachmentLocation #1 ->', {
    projectId,
    tenderId,
    contractId,
    fileGuid,
  });

  const res = await apiCweb.head(
    `${evalCurrentBase(
      projectId,
      tenderId,
      contractId
    )}/attachments/${fileGuid}/location`
  );

  const location =
    (res.headers?.location as string) ||
    (res.headers?.Location as string) ||
    '';

  appLog('getEvaluationCurrentInstantMessageAttachmentLocation #2 ->', {
    status: res.status,
    location,
  });
  return location;
};

// ==== OFFER FILE DOWNLOAD ====

export type OfferFileDownloadItem = {
  file_id: string;
  file_name: string;
  file_size: number;
  tender_id: number;
  offer_id: number;
  voucher_id: number;
  using_key: string;
};

export const getOfferFileDownloadList = async ({
  projectId,
  tenderId,
  offerId,
}: BaseProjectParams & { offerId: number }): Promise<
  OfferFileDownloadItem[]
> => {
  const res = await apiCweb.get<OfferFileDownloadItem[]>(
    `/projects/${projectId}/tenders/${tenderId}/offers/${offerId}/file_download`
  );
  appLog('getOfferFileDownloadList ->', res.data);
  return res.data;
};

export const getOffersBatchDownloadUrls = async ({
  projectId,
  tenderId,
  using_keys,
}: BaseProjectParams & { using_keys: string[] }): Promise<
  { key: string; url: string }[]
> => {
  const res = await apiCweb.post<{ key: string; url: string }[]>(
    `/projects/${projectId}/tenders/${tenderId}/offers/batch_download_urls`,
    { using_keys }
  );
  appLog('getOffersBatchDownloadUrls ->', res.data);
  return res.data;
};

// ==== OFFER REVIEWS ====

// Screening Phase Reviews (Binary)
export const getScreeningOfferReviews = async ({
  projectId,
  tenderId,
  contractId,
  offerId,
  skip = 0,
  take = 10,
  sort = 'desc(created_at)',
}: {
  projectId: number;
  tenderId: number;
  contractId: number;
  offerId: number;
  skip?: number;
  take?: number;
  sort?: string;
}) => {
  const res = await apiCweb.get(
    `/projects/${projectId}/tenders/${tenderId}/contracts/${contractId}/screening/offers/${offerId}/reviews`,
    { params: { skip, take, sort } }
  );
  appLog('getScreeningOfferReviews ->', res.data);
  return res.data;
};

export const upsertScreeningOfferReview = async ({
  projectId,
  tenderId,
  contractId,
  offerId,
  body,
}: {
  projectId: number;
  tenderId: number;
  contractId: number;
  offerId: number;
  body: {
    binary_rating: boolean;
    title: string;
    content?: string | null;
    summary?: string | null;
  };
}) => {
  const res = await apiCweb.put(
    `/projects/${projectId}/tenders/${tenderId}/contracts/${contractId}/screening/offers/${offerId}/reviews/current`,
    body
  );
  appLog('upsertScreeningOfferReview ->', res.status);
  return res.data;
};

export const deleteScreeningOfferReview = async ({
  projectId,
  tenderId,
  contractId,
  offerId,
}: {
  projectId: number;
  tenderId: number;
  contractId: number;
  offerId: number;
}) => {
  const res = await apiCweb.delete(
    `/projects/${projectId}/tenders/${tenderId}/contracts/${contractId}/screening/offers/${offerId}/reviews/current`
  );
  appLog('deleteScreeningOfferReview ->', res.status);
  return res.data;
};

// Evaluation Phase Reviews (Star Rating)
export const getEvaluationOfferReviews = async ({
  projectId,
  tenderId,
  contractId,
  offerId,
  skip = 0,
  take = 10,
  sort = 'desc(created_at)',
}: {
  projectId: number;
  tenderId: number;
  contractId: number;
  offerId: number;
  skip?: number;
  take?: number;
  sort?: string;
}) => {
  const res = await apiCweb.get(
    `/projects/${projectId}/tenders/${tenderId}/contracts/${contractId}/evaluation/offers/${offerId}/reviews`,
    { params: { skip, take, sort } }
  );
  appLog('getEvaluationOfferReviews ->', res.data);
  return res.data;
};

export const upsertEvaluationOfferReview = async ({
  projectId,
  tenderId,
  contractId,
  offerId,
  body,
}: {
  projectId: number;
  tenderId: number;
  contractId: number;
  offerId: number;
  body: {
    star_rating: 1 | 2 | 3 | 4 | 5;
    title: string;
    content?: string | null;
    summary?: string | null;
  };
}) => {
  const res = await apiCweb.put(
    `/projects/${projectId}/tenders/${tenderId}/contracts/${contractId}/evaluation/offers/${offerId}/reviews/current`,
    body
  );
  appLog('upsertEvaluationOfferReview ->', res.status);
  return res.data;
};

export const deleteEvaluationOfferReview = async ({
  projectId,
  tenderId,
  contractId,
  offerId,
}: {
  projectId: number;
  tenderId: number;
  contractId: number;
  offerId: number;
}) => {
  const res = await apiCweb.delete(
    `/projects/${projectId}/tenders/${tenderId}/contracts/${contractId}/evaluation/offers/${offerId}/reviews/current`
  );
  appLog('deleteEvaluationOfferReview ->', res.status);
  return res.data;
};

// ==== SCREENING REVIEW COMMENTS ====

export const createScreeningReviewComment = async ({
  projectId,
  tenderId,
  contractId,
  offerId,
  reviewId,
  body,
}: {
  projectId: number;
  tenderId: number;
  contractId: number;
  offerId: number;
  reviewId: number;
  body: {
    title?: string;
    summary?: string;
    description?: string;
  };
}) => {
  const res = await apiCweb.post(
    `/projects/${projectId}/tenders/${tenderId}/contracts/${contractId}/screening/offers/${offerId}/reviews/${reviewId}/comments`,
    body
  );
  appLog('createScreeningReviewComment ->', res.status);
  return res.status;
};

export const createScreeningReviewAnswer = async ({
  projectId,
  tenderId,
  contractId,
  offerId,
  reviewId,
  commentId,
  body,
}: {
  projectId: number;
  tenderId: number;
  contractId: number;
  offerId: number;
  reviewId: number;
  commentId: number;
  body: {
    title?: string;
    summary?: string;
    description?: string;
  };
}) => {
  const res = await apiCweb.post(
    `/projects/${projectId}/tenders/${tenderId}/contracts/${contractId}/screening/offers/${offerId}/reviews/${reviewId}/comments/${commentId}/answers`,
    body
  );
  appLog('createScreeningReviewAnswer ->', res.status);
  return res.status;
};

export const updateScreeningReviewComment = async ({
  projectId,
  tenderId,
  contractId,
  offerId,
  reviewId,
  commentId,
  body,
}: {
  projectId: number;
  tenderId: number;
  contractId: number;
  offerId: number;
  reviewId: number;
  commentId: number;
  body: {
    title?: string;
    summary?: string;
    description?: string;
  };
}) => {
  const res = await apiCweb.put(
    `/projects/${projectId}/tenders/${tenderId}/contracts/${contractId}/screening/offers/${offerId}/reviews/${reviewId}/comments/current/${commentId}`,
    body
  );
  appLog('updateScreeningReviewComment ->', res.status);
  return res.status;
};

export const deleteScreeningReviewComment = async ({
  projectId,
  tenderId,
  contractId,
  offerId,
  reviewId,
  commentId,
}: {
  projectId: number;
  tenderId: number;
  contractId: number;
  offerId: number;
  reviewId: number;
  commentId: number;
}) => {
  const res = await apiCweb.delete(
    `/projects/${projectId}/tenders/${tenderId}/contracts/${contractId}/screening/offers/${offerId}/reviews/${reviewId}/comments/current/${commentId}`
  );
  appLog('deleteScreeningReviewComment ->', res.status);
  return res.status;
};

// ==== EVALUATION REVIEW COMMENTS ====

export const createEvaluationReviewComment = async ({
  projectId,
  tenderId,
  contractId,
  offerId,
  reviewId,
  body,
}: {
  projectId: number;
  tenderId: number;
  contractId: number;
  offerId: number;
  reviewId: number;
  body: {
    title?: string;
    summary?: string;
    description?: string;
  };
}) => {
  const res = await apiCweb.post(
    `/projects/${projectId}/tenders/${tenderId}/contracts/${contractId}/evaluation/offers/${offerId}/reviews/${reviewId}/comments`,
    body
  );
  appLog('createEvaluationReviewComment ->', res.status);
  return res.status;
};

export const createEvaluationReviewAnswer = async ({
  projectId,
  tenderId,
  contractId,
  offerId,
  reviewId,
  commentId,
  body,
}: {
  projectId: number;
  tenderId: number;
  contractId: number;
  offerId: number;
  reviewId: number;
  commentId: number;
  body: {
    title?: string;
    summary?: string;
    description?: string;
  };
}) => {
  const res = await apiCweb.post(
    `/projects/${projectId}/tenders/${tenderId}/contracts/${contractId}/evaluation/offers/${offerId}/reviews/${reviewId}/comments/${commentId}/answers`,
    body
  );
  appLog('createEvaluationReviewAnswer ->', res.status);
  return res.status;
};

export const updateEvaluationReviewComment = async ({
  projectId,
  tenderId,
  contractId,
  offerId,
  reviewId,
  commentId,
  body,
}: {
  projectId: number;
  tenderId: number;
  contractId: number;
  offerId: number;
  reviewId: number;
  commentId: number;
  body: {
    title?: string;
    summary?: string;
    description?: string;
  };
}) => {
  const res = await apiCweb.put(
    `/projects/${projectId}/tenders/${tenderId}/contracts/${contractId}/evaluation/offers/${offerId}/reviews/${reviewId}/comments/current/${commentId}`,
    body
  );
  appLog('updateEvaluationReviewComment ->', res.status);
  return res.status;
};

export const deleteEvaluationReviewComment = async ({
  projectId,
  tenderId,
  contractId,
  offerId,
  reviewId,
  commentId,
}: {
  projectId: number;
  tenderId: number;
  contractId: number;
  offerId: number;
  reviewId: number;
  commentId: number;
}) => {
  const res = await apiCweb.delete(
    `/projects/${projectId}/tenders/${tenderId}/contracts/${contractId}/evaluation/offers/${offerId}/reviews/${reviewId}/comments/current/${commentId}`
  );
  appLog('deleteEvaluationReviewComment ->', res.status);
  return res.status;
};
